import { get } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Divider,
  Header,
  Loader,
  Pagination
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature";
import RedirectWithReset from "../../components/utils/RedirectWithReset";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import ActionDropdownMenu from "../../components/actionDropdownMenu/ActionDropdownMenu";
import ResourceMarker from "../../components/marking/ResourceMarker";
import UserFilters from "../../components/users/UserFilters";
import ChallengeConfirmationModal from "../../components/modals/ChallengeConfirmationModal";
import EntityUserList from "../../components/users/EntityUserList";

import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils";

import { AT_TYPES, RESOURCE_URI_CHUNKS } from "../../constants/entity";
import { OPERATIONS } from "../../constants/operations";
import { PROJECTS } from "../../state/actions/actionConstants";
import { ROUTES } from "../../constants/routing";

import { operationAction } from "../../state/reducers/operations/operationActions";
import { getProject } from "../../state/reducers/projects/projectsActions";
import { getEntityUsers } from "../../state/reducers/entity/entityActions";

import projectSelector from "../../state/reducers/projects/projectSelector";
import userSelector from "../../state/reducers/users/userSelector";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector";
import { selectCopy } from "../../state/reducers/locale/copySelector";

class ProjectUsers extends Component {
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    project: PropTypes.object,
    users: PropTypes.array,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    listProjectAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    getProject: PropTypes.func.isRequired,
    getEntityUsers: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "project_users"
  };

  constructor(props) {
    super(props);

    this.state = {
      filters: null,
      view: "card",
      redirect: null,

      atId: `/${RESOURCE_URI_CHUNKS.PROJECTS}/${props.match.params.id}`,
      cancelSettings: false,

      showModal: false,
      modalCopyKey: "",
      modalData: {},
      modalAction: null
    };
  }

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    // do search params change
    if (this.props.location.search !== prevProps.location.search) {
      this.getData();
    }
  }

  getData = () => {
    const {
      isFetching,
      isErrored,
      getProject,
      getEntityUsers,
      listProjectAvailability: { available },
      location
    } = this.props;

    if (!isFetching && !isErrored && available) {
      getProject(this.state.atId, false, false);
      getEntityUsers(this.state.atId, location.search);
    }
  };

  // -- HANDLERS & ACTIONS -- //

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  onCancel = () => {
    this.setState({ cancelSettings: true });
  };

  onSubmit = values => {
    const { location } = this.props;

    const data = {
      "@id": this.state.atId,
      ...values
    };
  };

  handleDelete = (id, type) => {
    const modalCopyKey = `projects.modals.delete_modal`;
    const modalData = { id, type };
    const modalAction = this.doDelete;

    this.setState(
      {
        modalCopyKey,
        modalData,
        modalAction
      },
      this.openModal
    );
  };

  doDelete = () => {
    const { project, operationAction } = this.props;
    operationAction(project, OPERATIONS.DELETE);
  };

  // -- MODAL -- //

  openModal = () => {
    this.setState({ showModal: true });
  };

  closeModal = confirmed => {
    const { modalAction } = this.state;

    if (confirmed === true) modalAction();

    this.setState({ showModal: false });
  };

  // -- RENDER -- //

  getBreadcrumbSections = () => {
    const { copy, project } = this.props;

    return [
      { content: copy("navigation.projects"), href: ROUTES.PROJECTS },
      {
        content: get(project, "name", "Unknown"),
        href: get(project, "@id", "")
      },
      { content: copy("navigation.project_users"), active: true }
    ];
  };

  renderHeaderSet = () => {
    const { project, copyKey, copy } = this.props;

    return (
      <Fragment>
        {project && (
          <Fragment>
            <Breadcrumbs sections={this.getBreadcrumbSections()} />

            <ActionDropdownMenu
              onDelete={this.handleDelete}
              viewPath={project["@id"]}
              data={project}
              copy={copy("project_dropdown_menu")}
            />

            <Header as="h3" className="action-dropdown-header">
              {`${project.name} ${copy(`${copyKey}.title`)}`}
            </Header>

            <ResourceMarker
              marking={project.marking}
              className="project-marker clearfix"
            />
          </Fragment>
        )}
        <Divider className="clear-both" />
      </Fragment>
    );
  };

  render() {
    const { atId, redirect, modalCopyKey, showModal, cancelSettings } =
      this.state;

    const {
      project,
      users,
      page,
      location,
      totalPages,
      isFetching,
      listProjectAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (cancelSettings === true) return <RedirectWithReset to={atId} />;

    if (!permitted) {
      return <UnauthorisedFeature title={copy("projects.title")} />;
    }

    // early return if not ready to render
    if (isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    // early return if no users are returned
    if (!enabled || !project || !users || users.length < 1) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <p>{copy(`${copyKey}.none_available`)}</p>
        </Container>
      );
    }

    return (
      <Container>
        {this.renderHeaderSet()}

        {users && (
          <Fragment>
            <Header as="h3">{copy(`${copyKey}.title`)}</Header>
            <EntityUserList
              entity={project}
              users={users}
              location={location}
            />
          </Fragment>
        )}

        {totalPages && totalPages > 1 && (
          <Fragment>
            <br />
            <Pagination
              activePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right clear-both"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}

        {showModal && (
          <ChallengeConfirmationModal
            entity={project}
            open={showModal}
            onClose={this.closeModal}
            copyKey={modalCopyKey}
          />
        )}
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  project: projectSelector.selectProject()(state, props),
  users: userSelector.selectUsers(state, props),

  page: state.projects.page,
  totalPages: state.projects.totalPages,
  timeReceived: `${state.projects.timeReceived}`,
  isFetching: state.projects.isFetching || state.ephemeralEntities.isFetching,
  isErrored: state.api.isError,
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProject: (id, usage, resources) =>
    dispatch(getProject(id, usage, resources)),
  getEntityUsers: (id, searchParams, dependencies) =>
    dispatch(getEntityUsers(id, searchParams, dependencies)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(PROJECTS, entity, operationName, data, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectUsers);
